import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button, Card, Pill, Spinner } from "@/components/ui-kit";
import QuestionCard from "@/components/QuestionCard";
import MathText from "@/components/MathText";
import { ArrowRight, CheckCircle2, Sparkles, Heart } from "lucide-react";
import { gradeShort } from "@/config";

const CHEERS = ["Nice effort! 🌱", "Great — keep going!", "You're doing brilliantly.", "Best guesses are perfect here.", "Love that thinking!"];

export default function Diagnostic() {
  const { studentId, reload } = useOutletContext();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [item, setItem] = useState(null);
  const [answer, setAnswer] = useState("");
  const [answered, setAnswered] = useState(0);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [begun, setBegun] = useState(false);
  const [cheer, setCheer] = useState("");

  useEffect(() => {
    api.post(`/students/${studentId}/diagnostic/start`).then(({ data }) => {
      setSession(data.session_id); setItem(data.item); setTotal(data.total_estimate);
    }).catch(() => {});
  }, [studentId]);

  const submit = async () => {
    if (!answer && item.type !== "mcq") return;
    setBusy(true);
    try {
      const { data } = await api.post(`/diagnostic/${session}/answer`, {
        skill_id: item.skill_id, item_id: item.item_id, answer,
      });
      if (data.done) {
        setDone(data);
        reload();
      } else {
        setItem(data.item);
        setAnswered(data.answered);
        setTotal((t) => Math.max(t, data.answered + 1));
        setAnswer("");
        setCheer(CHEERS[data.answered % CHEERS.length]);
      }
    } finally { setBusy(false); }
  };

  if (!item && !done) return <Spinner label="Preparing your diagnostic" />;

  if (item && !done && !begun) {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="diagnostic-intro">
        <Card className="p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl grid place-items-center mb-4" style={{ background: "var(--ns-primary-light)", color: "var(--ns-primary)" }}>
            <Sparkles size={30} />
          </div>
          <h1 className="font-head text-2xl font-bold">Let's warm up together</h1>
          <p className="text-[var(--ns-muted)] mt-2 max-w-sm mx-auto">
            I'll ask a few questions to find your perfect starting point. This isn't a test — just answer what you can, and a best guess is always okay.
          </p>
          <div className="mt-5 grid gap-2 text-sm text-left max-w-xs mx-auto">
            {["No timer, no pressure", "It's fine to get things wrong", "This shapes your personal plan"].map((t) => (
              <div key={t} className="flex items-center gap-2 text-[var(--ns-muted)]"><Heart size={15} className="text-[#D96B43]" /> {t}</div>
            ))}
          </div>
          <Button className="mt-6 mx-auto px-8" data-testid="diagnostic-begin-btn" onClick={() => setBegun(true)}>I'm ready <ArrowRight size={18} /></Button>
        </Card>
      </motion.div>
    );
  }

  if (done) {
    const gaps = done.gap_map || [];
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="diagnostic-result">
        <Card className="p-7 text-center mb-5">
          <CheckCircle2 size={44} className="mx-auto text-[#2A8555]" />
          <h1 className="font-head text-2xl font-bold mt-3">Your gap map is ready</h1>
          <p className="text-[var(--ns-muted)] mt-2">We found {gaps.length} skill{gaps.length === 1 ? "" : "s"} to focus on, ordered so foundations come first.</p>
        </Card>
        <div className="grid gap-3 mb-6">
          {gaps.slice(0, 6).map((g, i) => (
            <Card key={g.skill_id} className="p-4" data-testid={`gap-${g.skill_id}`}>
              <div className="flex items-center justify-between">
                <span className="font-head font-semibold">{g.name}</span>
                <Pill tone="gap">{gradeShort(g.grade)} · {g.strand}</Pill>
              </div>
              <p className="text-sm text-[var(--ns-muted)] mt-1"><b>Why this matters next:</b> <MathText text={g.why_it_matters} /></p>
            </Card>
          ))}
          {gaps.length === 0 && <Card className="p-5 text-center text-[var(--ns-muted)]">No gaps found — you're ready for grade-level challenges!</Card>}
        </div>
        <Button className="w-full" data-testid="goto-path-btn" onClick={() => nav(`/student/${studentId}/today`)}>Start my plan <ArrowRight size={18} /></Button>
      </motion.div>
    );
  }

  const progress = Math.min(100, Math.round((answered / Math.max(total, 1)) * 100));
  return (
    <div>
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm text-[var(--ns-muted)] mb-2">
          <span className="font-semibold">Diagnostic · {item.skill_name}</span>
          <span>{answered} answered</span>
        </div>
        <div className="h-2 rounded-full bg-[var(--ns-surface-subtle)] overflow-hidden">
          <div className="h-full bg-[var(--ns-primary)] transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        {cheer && (
          <motion.div key={cheer + answered} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm font-semibold text-[var(--ns-primary)]" data-testid="diagnostic-cheer">{cheer}</motion.div>
        )}
      </div>
      <Card className="p-6">
        <QuestionCard item={item} value={answer} onChange={setAnswer} disabled={busy} />
        <Button className="mt-6 w-full" data-testid="diagnostic-next-btn" disabled={busy || (!answer && item.type !== "mcq") || (item.type === "mcq" && !answer)} onClick={submit}>
          {busy ? "…" : "Submit"} <ArrowRight size={18} />
        </Button>
        <p className="text-xs text-center text-[var(--ns-subtle)] mt-3">Not sure? Give your best guess — this just helps us place you.</p>
      </Card>
    </div>
  );
}
